import { useGetDownloadSignedUrlForExamCourseComment } from '@exam-room/graphql/preview';
import DOMPurify from 'dompurify';
import katex from 'katex';
// eslint-disable-next-line @typescript-eslint/ban-ts-comment
// @ts-ignore
import autoRender from 'katex/dist/contrib/auto-render.min';
import { useCallback, useEffect, useState } from 'react';

export default function CommentRichTextView({
  content,
  taskId,
}: {
  content: string;
  taskId: string;
}) {
  if (!content) return null;

  const [getDownloadSignedUrls] = useGetDownloadSignedUrlForExamCourseComment();
  const [sanitizedHtml, setSanitizedHtml] = useState<string>('');

  function updateImageTags(content: string, urlObject: Record<string, string>) {
    const parser = new DOMParser();
    const doc = parser.parseFromString(content, 'text/html');
    const images = doc.getElementsByTagName('img');

    for (const img of Array.from(images)) {
      const contentAttr = img.getAttribute('content');
      if (contentAttr && urlObject[contentAttr]) {
        img.setAttribute('src', urlObject[contentAttr]);
        // Optionally drop the non-standard attribute after use
        img.removeAttribute('content');
      }
    }

    return doc.body.innerHTML;
  }

  function extractImagesFileNames(content: string) {
    const parser = new DOMParser();
    const doc = parser.parseFromString(content, 'text/html');
    const images = doc.getElementsByTagName('img');

    const filenames: string[] = [];

    for (const img of Array.from(images)) {
      const contentAttr = img.getAttribute('content');
      if (contentAttr) {
        filenames.push(contentAttr);
      }
    }

    return [...new Set(filenames)]; // Remove duplicates
  }

  const parseCommentContent = useCallback(
    async (rawContent: string) => {
      const fileNames = extractImagesFileNames(rawContent);

      if (fileNames.length === 0) {
        return rawContent;
      }

      const { data } = await getDownloadSignedUrls({
        variables: {
          getDownloadSignedUrlForExamCourseCommentInput: {
            fileNames: fileNames,
            taskId,
          },
        },
      });

      const urlObject: Record<string, string> | undefined =
        data?.excGetDownloadSignedUrlForExamCourseComment?.signedUrls;

      if (!urlObject) {
        return rawContent;
      }

      return updateImageTags(rawContent, urlObject);
    },
    [taskId, getDownloadSignedUrls],
  );

  useEffect(() => {
    let canceled = false;

    (async () => {
      // 1) Parse and replace image URLs
      const processed = await parseCommentContent(content);

      // 2) Render LaTeX: handle both <span data-latex="..."> and $...$ delimiters
      const parser = new DOMParser();
      const doc = parser.parseFromString(processed, 'text/html');

      const latexNodes = doc.querySelectorAll<HTMLElement>('[data-latex]');
      for (const node of Array.from(latexNodes)) {
        const latex = node.getAttribute('data-latex');
        if (latex) {
          try {
            const html = katex.renderToString(latex, { throwOnError: false });
            node.innerHTML = html;
            node.removeAttribute('data-latex');
          } catch {
            // ignore invalid latex
          }
        }
      }

      try {
        autoRender(doc.body, {
          throwOnError: false,
          delimiters: [
            { left: '$$', right: '$$', display: true },
            { left: '$', right: '$', display: false },
            { left: '\\(', right: '\\)', display: false },
            { left: '\\[', right: '\\]', display: true },
          ],
        });
      } catch {
        // ignore
      }

      const sanitized = typeof window === 'undefined' ? '' : DOMPurify.sanitize(doc.body.innerHTML);

      if (!canceled) {
        setSanitizedHtml(sanitized);
      }
    })();

    return () => {
      canceled = true;
    };
  }, [content, parseCommentContent]);

  if (!sanitizedHtml) return null;

  return (
    <div className='rte-content max-w-none' dangerouslySetInnerHTML={{ __html: sanitizedHtml }} />
  );
}
